import type { CoverDoc } from 'fym-shared/src/music24/coverDoc';
import { drawCoverBackground, type CoverPicture } from './drawBackground';
import { drawCoverText } from './drawText';
import { drawCoverWatermark } from './drawWatermark';
import { drawCoverEffect } from './effects/drawEffects';

export interface CoverRenderAssets {
  /** The loaded picture of a picture background; null for a colour, a gradient, or while it loads. */
  picture: CoverPicture | null;
}

export interface CoverRenderOptions {
  /** Draws the Music24 watermark (the download of Free, the trial and the public tool). */
  watermark?: boolean;
  /** A text box that is left out: the stage hides the box that the user types in. */
  hiddenTextId?: string | null;
}

/**
 * THE renderer of the cover: one function draws the screen preview and the exported file, so both
 * are the same picture. It draws a square of `side` pixels at the origin of the context, in this
 * order: the background, the effects under the text, the text boxes, the effects over the text,
 * the watermark. Every size in the document is a share of the side. The kit fonts must be loaded
 * before the call (`loadKitWebFonts`); a font that is not loaded draws in its fallback.
 */
export function renderCoverDoc(
  ctx: CanvasRenderingContext2D,
  doc: CoverDoc,
  side: number,
  assets: CoverRenderAssets,
  options: CoverRenderOptions = {}
): void {
  ctx.save();
  // Nothing may draw outside the square.
  ctx.beginPath();
  ctx.rect(0, 0, side, side);
  ctx.clip();
  drawCoverBackground(ctx, doc.background, side, assets.picture);
  doc.effects.filter((effect) => effect.layer === 'underText').forEach((effect) => drawCoverEffect(ctx, effect, side));
  doc.texts.filter((box) => box.id !== options.hiddenTextId).forEach((box) => drawCoverText(ctx, box, side));
  doc.effects.filter((effect) => effect.layer === 'overText').forEach((effect) => drawCoverEffect(ctx, effect, side));
  if (options.watermark === true) {
    drawCoverWatermark(ctx, side);
  }
  ctx.restore();
}
